import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { camelCase } from 'lodash-es';
import React, { forwardRef, Ref, useEffect, useImperativeHandle, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { FormStateChangeProps } from '~formsShared/types';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { BasicEquipment, Equipment } from '~hooks/useEquipment/models';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import { EquipmentHint } from '~pages/Settings/Administration/EquipmentManagement/EquipmentForm';
import { externalEquipmentFormValidationSchema } from '~pages/Settings/Administration/EquipmentManagement/equipmentFormValidationSchema';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

export type ExternalEquipmentDTO = yup.InferType<
  typeof externalEquipmentFormValidationSchema
>;

export interface ExternalEquipmentFormHandlers {
  submit: (
    callBack: (
      data: ExternalEquipmentDTO,
      dirtyFields: Partial<Record<string, unknown>>,
    ) => void,
  ) => void;
  resetForm: (callBack?: () => void) => void;
}

interface ExternalEquipmentFormProps {
  defaultEquipment: Nullable<Equipment | BasicEquipment>;
  companyShares: Nullable<Array<CompanyShare>>;
  onFormStateChange: ({ isValid, isDirty }: FormStateChangeProps) => void;
  onFormDirty?: (isDirty: boolean) => void;
  selectedCompanyId: Nullable<string>;
  errors?: ModelError_Item[];
  managedCompanyId?: string;
}

const ExternalEquipmentForm = forwardRef(function ExternalEquipmentForm(
  {
    defaultEquipment,
    companyShares,
    onFormStateChange,
    selectedCompanyId,
    errors: errorsProp,
    managedCompanyId,
  }: ExternalEquipmentFormProps,
  ref: Ref<ExternalEquipmentFormHandlers>,
) {
  const [connectedCompanies, setConnectedCompanies] = useState<CompanyBasic[]>([]);
  const { userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
  const {
    getAllConnectedCompaniesByCompanyId,
    getAllConnectedCompaniesByCompanyIdTypeahead,
  } = useCompany();
  const isSharedEquipment = !!defaultEquipment?.companyShare?.id;
  const selectedCompanyOwnsEquipment =
    (defaultEquipment as Equipment)?.company?.id === selectedCompanyId;

  const defaultValues = {
    id: defaultEquipment?.id || '',
    name: defaultEquipment?.name || '',
    externalId: defaultEquipment?.companyShare?.externalId || '',
    ticketMatchId: defaultEquipment?.companyShare?.ticketMatchId || '',
    companyShares: companyShares?.length
      ? companyShares.map((companyShare) => {
          return {
            id: companyShare.id,
            receiverCompany: {
              id: companyShare.receiverCompany.id,
              legalName: companyShare.receiverCompany.legalName,
            },
            dispatchable: {
              id: companyShare.dispatchable ? 'dispatchable' : 'view_only',
              name: companyShare.dispatchable
                ? t('common.dispatchable')
                : t('common.view_only'),
            },
            ticketMatchId: companyShare.ticketMatchId || '',
            companyShareableId: companyShare.companyShareable.id,
          };
        })
      : [],
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, dirtyFields },
    reset,
    setError,
  } = useForm({
    resolver: yupResolver(externalEquipmentFormValidationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const ownerCompany =
    defaultEquipment && 'internalCompany' in defaultEquipment
      ? defaultEquipment.internalCompany
      : null;
  const sharerCompany = defaultEquipment?.companyShare?.senderCompany ?? null;
  const viewerIsTheOwner = ownerCompany?.id === effectiveCompanyId;
  const sharerDiffersFromOwner = Boolean(
    ownerCompany && sharerCompany && sharerCompany.id !== ownerCompany.id,
  );

  const attributionHeading = () => {
    if (viewerIsTheOwner) {
      return t('administration.equipment.owner_card_owned_by_you');
    }

    if (ownerCompany) {
      return t('administration.equipment.owner_card_owned_by', {
        company: ownerCompany.legalName,
        interpolation: { escapeValue: false },
      });
    }

    if (sharerCompany) {
      return t('administration.equipment.owner_card_shared_by', {
        company: sharerCompany.legalName,
        interpolation: { escapeValue: false },
      });
    }

    return t('administration.equipment.owner_card_other_company');
  };

  const ownerCardRows = [
    {
      label: t('administration.equipment.owner_card_name'),
      value: defaultEquipment?.name,
    },
    ...(sharerDiffersFromOwner
      ? [
          {
            label: t('administration.equipment.shared_by'),
            value: sharerCompany?.legalName,
          },
        ]
      : []),
    {
      label: t('administration.equipment.type'),
      value:
        defaultEquipment && 'type' in defaultEquipment
          ? defaultEquipment.type?.name
          : defaultEquipment?.equipmentType?.name,
    },
    {
      label: t('administration.equipment.license_plate'),
      value: defaultEquipment?.licenseNumber,
    },
    {
      label: t('administration.equipment.your_access_level'),
      value: defaultEquipment?.companyShare?.dispatchable
        ? t('common.dispatchable')
        : t('administration.equipment.view_only'),
    },
  ];

  // Set errors from props
  useEffect(() => {
    if (errorsProp) {
      for (const error of errorsProp) {
        const field = camelCase(error.field) as keyof ReturnType<typeof setError>;
        // Replace external ID with Equipment ID
        const message = error.message.replace('external', 'Equipment');

        if (field && message) {
          setError(field, { type: 'manual', message });
        }
      }
    }
  }, [errorsProp]);

  // Update default values once async companyShares are loaded
  useEffect(() => {
    reset(defaultValues);
  }, [companyShares]);

  const {
    fields: companySharesFields,
    append: appendCompanyShares,
    remove: removeCompanyShares,
  } = useFieldArray({
    control,
    name: 'companyShares',
  });

  useImperativeHandle(ref, () => ({
    submit(callback) {
      handleSubmit((data) => callback(data as ExternalEquipmentDTO, dirtyFields))();
    },
    resetForm(callBack?: () => void) {
      reset();
      callBack?.();
    },
  }));

  useEffect(() => {
    const companyId = managedCompanyId
      ? effectiveCompanyId
      : selectedCompanyId || userStore?.userCompany?.id;

    if (companyId) {
      getAllConnectedCompaniesByCompanyId({
        companyId,
        managedCompanyId,
        callback: setConnectedCompanies,
      });
    }
  }, [userStore?.userCompany?.id, selectedCompanyId, managedCompanyId]);

  useEffect(() => {
    onFormStateChange({ isValid, isDirty });
  }, [isValid, isDirty]);

  return (
    <form>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          flexGrow: 1,
        }}
      >
        <Box
          data-test-id="external-equipment-owner-card"
          sx={(theme) => ({
            backgroundColor: theme.semantic.surfaceSunken,
            border: `1px solid ${theme.semantic.border}`,
            borderRadius: 1,
            p: 2,
          })}
        >
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
            {attributionHeading()}
          </Typography>

          <Box sx={{ mt: 1.5 }}>
            {ownerCardRows.map((item) => {
              return (
                <Box
                  key={item.label}
                  sx={{
                    mt: 0.5,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                  }}
                >
                  <Typography variant={'body1'} sx={{ fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  <Typography variant={'body1'}>{item.value}</Typography>
                </Box>
              );
            })}
          </Box>

          <EquipmentHint sx={{ mt: 1.5 }}>
            {t('administration.equipment.owner_card_read_only')}
          </EquipmentHint>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
            {t('administration.equipment.your_details_heading')}
          </Typography>
          <EquipmentHint sx={{ mt: 0.5 }}>
            {t('administration.equipment.your_details_desc')}
          </EquipmentHint>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Box sx={{ width: '50%' }}>
              <TextFormField
                control={control}
                errors={errors}
                name="externalId"
                label={`${t('form_fields.truck_name_override')}`}
                isRequired={false}
              />
            </Box>
            <Box sx={{ width: '50%' }}>
              <TextFormField
                control={control}
                errors={errors}
                name="ticketMatchId"
                label={`${t('form_fields.your_ticket_match_number')}`}
                isRequired={false}
              />
              <EquipmentHint>
                {t('administration.equipment.ticket_match_number_hint')}
              </EquipmentHint>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{ fontSize: '16px', fontWeight: 600 }}
                >{`${t('administration.equipment.your_onward_shares')}`}</Typography>
                <EquipmentHint
                  sx={{ mt: 0.5 }}
                >{`${t('administration.equipment.your_onward_shares_desc')}`}</EquipmentHint>
              </Box>

              {companySharesFields.map((item, index) => {
                return (
                  <Box
                    key={item.id}
                    sx={{ display: 'flex', gap: 1, width: '100%', mt: 2 }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <AutocompleteAsyncFormField
                        control={control}
                        errors={errors}
                        name={`companyShares[${index}].receiverCompany`}
                        getValue={(item) => item.id}
                        getLabel={(item) => item.legalName}
                        asyncCallback={getAllConnectedCompaniesByCompanyIdTypeahead}
                        extraRequestOptions={{
                          companyId: managedCompanyId
                            ? effectiveCompanyId
                            : selectedCompanyId || userStore.userCompany.id || '',
                          managedCompanyId,
                        }}
                        label={`${t('form_fields.share_to_company')}`}
                        disabled={
                          !!(
                            defaultValues.companyShares.length && item.receiverCompany.id
                          )
                        } // Disable existing company shares
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <TextFormField
                        control={control}
                        errors={errors}
                        name={`companyShares[${index}].ticketMatchId`}
                        label={`${t('form_fields.receiver_ticket_match_number')}`}
                        isRequired={false}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <AutocompleteFormField
                        control={control}
                        errors={errors}
                        name={`companyShares[${index}].dispatchable`}
                        list={[
                          {
                            id: 'dispatchable',
                            name: t('common.dispatchable'),
                          },
                          {
                            id: 'view_only',
                            name: t('common.view_only'),
                          },
                        ]}
                        isRequired={false}
                        getValue={(item) => item.id}
                        getLabel={(item) => item.name}
                        label={`${t('form_fields.access_level')}`}
                        disabled={
                          isSharedEquipment &&
                          !selectedCompanyOwnsEquipment &&
                          !defaultEquipment?.companyShare?.dispatchable
                        }
                      />
                    </Box>
                    <RemoveItemButton
                      action={() => {
                        removeCompanyShares(index);
                      }}
                      sx={{ cursor: 'pointer', mt: 2.3 }}
                    />
                  </Box>
                );
              })}
            </Box>

            <PseudoLink
              disabled={companySharesFields.length === connectedCompanies.length}
              title={`+ ${t('administration.equipment.add_shared_company')}`}
              sx={{
                mt: 1,
                display: 'inline-flex',
                pointerEvents:
                  companySharesFields.length === connectedCompanies.length
                    ? 'none'
                    : 'auto',
              }}
              action={() => {
                companySharesFields.length !== connectedCompanies.length &&
                  appendCompanyShares({
                    id: '',
                    receiverCompany: { id: '', legalName: '' },
                    dispatchable: { id: 'view_only', name: 'View Only' },
                    ticketMatchId: '',
                    companyShareableId: defaultEquipment?.id || '',
                  });
              }}
            />
          </Box>
        </Box>
      </Box>
    </form>
  );
});
export { ExternalEquipmentForm };
